import { ActionIcon, AsyncDataTable, Box, Button, EmptyState, LoadError, Text, Tooltip, modals } from "@dehub/theme";
import type { AsyncDataTableRef, DataTableColumn } from "@dehub/theme";
import { PencilSimpleIcon, TrashIcon, UserPlusIcon, WarningIcon } from "@phosphor-icons/react";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { roleLabel } from "./role-groups";
import { useSelector } from "react-redux";
import { BusinessApi } from "@/apis";
import type { IFilter } from "@/models/Filter";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import classes from "../business.module.css";

type IRoleMeta = { id: string; name: string; code?: string };

type IUserMeta = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  departmentUnitId?: string | null;
  employeeUnitId?: string | null;
  accessLevel?: string | null;
  roles?: IRoleMeta[];
};

export type IStaffRow = {
  id: string;
  userId: string;
  user?: IUserMeta;
};

type Props = Readonly<{
  businessId: string;
  onEdit: (row: IStaffRow) => void;
  onAdd: () => void;
}>;

export const StaffTable = forwardRef<AsyncDataTableRef, Props>(({ businessId, onEdit, onAdd }, ref) => {
  const { t } = useTranslation("staff");
  const { t: tCommon } = useTranslation("common");
  const tableRef = useRef<AsyncDataTableRef | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  useImperativeHandle(ref, () => ({ reload: () => tableRef.current?.reload() }), []);
  const accessLevels = useSelector((state: { general: IGeneral }) => state.general.accessLevels);
  const isDehubManager = useSelector((state: { general: IGeneral }) => state.general.isDehubManager);

  const openDelete = (row: IStaffRow) => {
    modals.openConfirmModal({
      withCloseButton: false,
      centered: true,
      radius: "lg",
      title: (
        <Box component="span" className={`${classes.tokens} ${classes.confirmTitleRow}`}>
          <Box component="span" className={`${classes.confirmIcon} ${classes.confirmIconOff}`}>
            <WarningIcon size={20} weight="bold" aria-hidden="true" />
          </Box>
          <Text component="span" className={classes.confirmTitle}>
            {t("user.removeTitle")}
          </Text>
        </Box>
      ),
      children: (
        <Box className={`${classes.tokens} ${classes.confirm}`}>
          <Text className={classes.confirmText}>{t("user.removeText")}</Text>
          <Box className={classes.summaryCard}>
            <Box className={classes.summaryText}>
              <Text component="span" className={classes.summaryName}>
                {renderName(row.user)}
              </Text>
              <Text component="span" className={classes.mono}>
                {row.user?.phone ?? ""}
              </Text>
            </Box>
          </Box>
        </Box>
      ),
      labels: { confirm: t("user.removeBtn"), cancel: tCommon("actions.cancel") },
      confirmProps: { color: "red", className: classes.btn },
      cancelProps: { className: classes.btn },
      onConfirm: async () => {
        try {
          const res = await BusinessApi.deleteUser(row.id);
          if (res?.success) {
            message.success(t("msg.userDeleteSuccess"));
            tableRef.current?.reload();
          }
        } catch (error: unknown) {
          message.error((error as { message?: string })?.message ?? t("msg.genericError"));
        }
      },
    });
  };

  const accessName = (code?: string | null) => (accessLevels ?? []).find((a) => a.code === code)?.name ?? code ?? "";

  const columns = useMemo<DataTableColumn<IStaffRow>[]>(
    () => [
      {
        key: "actions",
        header: tCommon("col.action"),
        width: 96,
        render: (row) => (
          <Box className={classes.rowActions}>
            <Tooltip label={tCommon("actions.edit")} withArrow>
              <ActionIcon variant="subtle" color="gray" size={30} aria-label={tCommon("actions.edit")} onClick={() => onEdit(row)}>
                <PencilSimpleIcon size={16} />
              </ActionIcon>
            </Tooltip>
            <ReasonTooltip reason={isDehubManager ? t("user.removeLocked") : null}>
              <ActionIcon variant="subtle" color="gray" size={30} aria-label={t("actions.delete")} onClick={() => openDelete(row)}>
                <TrashIcon size={16} />
              </ActionIcon>
            </ReasonTooltip>
          </Box>
        ),
      },
      {
        key: "staff",
        header: t("user.colStaff"),
        render: (row) => (
          <Box className={classes.staffName}>
            <Text component="span" fw={600}>
              {renderName(row.user)}
            </Text>
            <Text component="span" className={classes.mono}>
              {row.user?.phone ?? ""}
            </Text>
          </Box>
        ),
      },
      {
        key: "unit",
        header: t("user.colUnit"),
        // These two columns hold names, not foreign keys: shown as they are.
        render: (row) => (
          <Text component="span" className={classes.muted}>
            {[row.user?.departmentUnitId, row.user?.employeeUnitId].filter(Boolean).join(" · ") || "-"}
          </Text>
        ),
      },
      {
        key: "roles",
        header: t("user.colRole"),
        render: (row) => (
          <Box className={classes.chips}>
            {(row.user?.roles ?? []).map((role) => (
              <Box key={role.id} component="span" className={classes.chip}>
                {roleLabel(role, true)}
              </Box>
            ))}
          </Box>
        ),
      },
      {
        key: "accessLevel",
        header: t("user.accessLevel"),
        render: (row) => (
          <Text component="span" className={classes.muted}>
            {accessName(row.user?.accessLevel) || "-"}
          </Text>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tCommon, accessLevels, isDehubManager, onEdit],
  );

  const loadData = async (data: { offset: { page: number; limit: number } }) => {
    setLoadError(null);
    try {
      const res = (await BusinessApi.staffs(data as IFilter, businessId)) as { count?: number; rows?: IStaffRow[]; found?: IStaffRow[] };
      // GET /prt/business/staffs answers { count, found }; AsyncDataTable reads { rows, count }.
      return { rows: res.rows ?? res.found ?? [], count: res.count ?? 0 };
    } catch (error: unknown) {
      // A failed request is an error state with a retry, not an empty list: "no staff" would be a lie.
      const text = (error as { message?: string })?.message ?? t("msg.staffFetchError");
      setLoadError(text);
      message.error(text);
      return { rows: [], count: 0 };
    }
  };

  const retry = () => {
    setLoadError(null);
    tableRef.current?.reload();
  };

  return (
    <Box className={classes.tableWrap}>
      <AsyncDataTable<IStaffRow>
        ref={tableRef}
        rowKey={(r) => r.id}
        cardPadding={0}
        columns={columns}
        // businessId is part of the table's request key, so another business never shows a cached answer.
        filters={{ businessId }}
        loadData={loadData}
        tableProps={{ striped: false, highlightOnHover: true, verticalSpacing: "sm", stickyHeader: false }}
        emptyState={
          loadError ? (
            <LoadError title={t("user.loadErrorTitle")} message={loadError} onRetry={retry} retryLabel={t("user.retry")} />
          ) : (
            <EmptyState
              icon={<UserPlusIcon size={24} aria-hidden="true" />}
              title={t("user.emptyTitle")}
              description={t("user.emptyText")}
              action={
                <Button variant="default" onClick={onAdd}>
                  {t("user.addBtn")}
                </Button>
              }
            />
          )
        }
      />
    </Box>
  );
});

StaffTable.displayName = "StaffTable";
